import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { Button } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The add-to-cart affordance, at both the sizes a storefront asks for it.
 *
 *   <ProductAddToCart variant={ProductAddToCartVariant.PANEL} onClick={…}>
 *     <CartIcon className="size-4 shrink-0 @sm:size-5" />
 *     <ProductAddToCartLabel variant={ProductAddToCartVariant.PANEL}>
 *       Do košíku
 *     </ProductAddToCartLabel>
 *   </ProductAddToCart>
 *
 *   <ProductAddToCart variant={ProductAddToCartVariant.ROW} onClick={…}>
 *     <ProductAddToCartMark>
 *       <CartIcon className="size-5 shrink-0" />
 *       <ProductAddToCartCount>2</ProductAddToCartCount>
 *     </ProductAddToCartMark>
 *     <ProductAddToCartLabel variant={ProductAddToCartVariant.ROW}>
 *       Koupit za 2 890 Kč
 *     </ProductAddToCartLabel>
 *   </ProductAddToCart>
 *
 * One part rather than two sets of parts. The panel's button and the
 * suggestion row's button are the same decision at two sizes — a full-width
 * one that asks, a compact one that points — and that is what an appearance
 * axis is for. Extracting them apart would have produced two near-identical
 * CTA families over one cart mutation, which is the duplication the image
 * fallback flow already caused once.
 *
 * The mutation itself is never here. A tenant owns the cart, the stock
 * arithmetic and the copy; these parts own how the affordance is painted, and
 * they take `onClick`, `disabled` and `loading` like the native control they
 * render.
 *
 * The label wraps rather than setting the section's minimum width. A CTA that
 * cannot wrap runs past its own clip and is cut mid-word, so the button is
 * `h-auto` over a minimum height — two lines survive — and `max-w-full` keeps
 * it inside whatever column holds it.
 */

/**
 * How the button paints in each appearance.
 *
 * The heights are minimums rather than fixed: at one line each resolves to the
 * same box it always did, and a label too long for its column takes a second
 * line instead of overflowing.
 */
export const productAddToCartVariants = cva('', {
  variants: {
    variant: {
      /** The panel's own: full width beside the quantity stepper. */
      panel: 'h-auto min-h-12 w-full max-w-full px-2 @md:min-h-16 @md:px-10',
      /**
       * The one that closes a column: full width like the panel's and at its
       * type scale, but only as tall as a control needs to be — it sits under a
       * short stack of words rather than beside a quantity stepper, and a
       * 64px button there reads as a second block rather than as the way out
       * of the one above it.
       */
      slim: 'h-auto min-h-12 w-full max-w-full px-2',
      /** A suggestion row's: compact, stated in outline beside the copy. */
      row: 'hover:border-primary hover:bg-primary/10 h-auto min-h-12 max-w-full gap-3 px-4 text-base/5 tracking-wide disabled:opacity-100',
      unstyled: '',
    },
  },
});

type ProductAddToCartVariantName = NonNullable<
  VariantProps<typeof productAddToCartVariants>['variant']
>;

export type ProductAddToCartVariant = ProductAddToCartVariantName;

/** `satisfies` binds these to the recipes above, so the two cannot drift. */
export const ProductAddToCartVariant = {
  PANEL: 'panel',
  SLIM: 'slim',
  ROW: 'row',
  /**
   * The opt-out: a tenant painting the control itself. Carried as an empty
   * entry rather than left out, so what it paints — nothing — is stated in the
   * list instead of decided by absence from it.
   */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, ProductAddToCartVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type ProductAddToCartVariantProps = { readonly variant: ProductAddToCartVariant };

/**
 * Which of `Button`'s own appearances each arm asks for. A mapping rather than
 * a recipe arm, because these are the primitive's classes and this says only
 * which of them the appearance wants — the same shape the product card uses for
 * `PriceTag`'s alignment.
 */
const buttonAppearanceByVariant = {
  panel: { variant: 'primary', size: '2xl' },
  slim: { variant: 'primary', size: 'base' },
  row: { variant: 'ghost', size: 'base' },
  // The opt-out asks for nothing, so `Button`'s own defaults stand. Named here
  // for the same reason the recipes name it: an appearance the map omits is one
  // the reader has to infer.
  unstyled: {},
} as const satisfies Record<
  ProductAddToCartVariant,
  Partial<Pick<ComponentProps<typeof Button>, 'variant' | 'size'>>
>;

export type ProductAddToCartProps = Omit<ComponentProps<typeof Button>, 'variant' | 'size'> &
  ProductAddToCartVariantProps;

/** The control that puts the product in the cart. */
export const ProductAddToCart = ({ className, variant, ...props }: ProductAddToCartProps) => (
  <Button
    data-slot="product-add-to-cart"
    {...buttonAppearanceByVariant[variant]}
    className={clsxm(productAddToCartVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The frame the cart mark hangs in, and the positioning context its count
 * badge is pinned to. Load-bearing and invisible when missing: a composition
 * that dropped it would still render the badge, pinned to whatever ancestor
 * happened to be positioned.
 */
const productAddToCartMarkClasses = 'relative';

export type ProductAddToCartMarkProps = ComponentProps<'div'>;

/** The cart glyph's box. No `variant`: it frames the same wherever it is used. */
export const ProductAddToCartMark = ({ className, ...props }: ProductAddToCartMarkProps) => (
  <div
    data-slot="product-add-to-cart-mark"
    className={clsxm(productAddToCartMarkClasses, className)}
    {...props}
  />
);

/** How many of this product the cart already holds, over the glyph's corner. */
const productAddToCartCountClasses =
  'bg-primary absolute -top-2 -right-2 flex items-center justify-center rounded-full px-1.5 py-0.5 ' +
  'text-primary-foreground ring-background text-xs/[13px] font-bold ring-2';

export type ProductAddToCartCountProps = ComponentProps<'div'>;

/** The quantity-in-cart badge. */
export const ProductAddToCartCount = ({ className, ...props }: ProductAddToCartCountProps) => (
  <div
    data-slot="product-add-to-cart-count"
    className={clsxm(productAddToCartCountClasses, className)}
    {...props}
  />
);

/** What the control says, sized for the appearance it sits in. */
export const productAddToCartLabelVariants = cva('', {
  variants: {
    variant: {
      panel: 'text-base uppercase @sm:text-[22px]',
      /** The same words at the same scale; only the button under them is shorter. */
      slim: 'text-base uppercase @sm:text-[22px]',
      /** The row states its offer at the button's own size. */
      row: '',
      unstyled: '',
    },
  },
});

export type ProductAddToCartLabelProps = ComponentProps<'span'> & ProductAddToCartVariantProps;

/** The control's copy. */
export const ProductAddToCartLabel = ({
  className,
  variant,
  ...props
}: ProductAddToCartLabelProps) => (
  <span
    data-slot="product-add-to-cart-label"
    className={optionalClassName(clsxm(productAddToCartLabelVariants({ variant }), className))}
    {...props}
  />
);
